// v1.9.0 · Página pública de reservas (/reservar/:slug). Sin sesión: el cliente elige día y hora libres y deja sus
// datos. La cita entra al CRM (contacto, cita y aviso no leído en Conversaciones).
// v1.19.0 · preguntas opcionales (campos personalizados «En formularios») y utm del enlace → campos del contacto.
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { CalendarDays, Clock, MapPin, CheckCircle2, ChevronLeft } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/public/booking`;
const TZ = 'America/Guayaquil';
const DAYS = ['lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado', 'domingo'];
const MONTHS = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const parts = new Intl.DateTimeFormat('en-CA', { timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
const p = (iso) => { const x = Object.fromEntries(parts.formatToParts(new Date(iso)).map((v) => [v.type, v.value])); return { date: `${x.year}-${x.month}-${x.day}`, time: `${x.hour}:${x.minute}` }; };
const addDays = (ymd, n) => { const d = new Date(`${ymd}T12:00:00Z`); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };
const wd = (ymd) => (new Date(`${ymd}T12:00:00Z`).getUTCDay() + 6) % 7;
const longDay = (ymd) => { const [, m, d] = ymd.split('-'); const t = `${DAYS[wd(ymd)]} ${Number(d)} de ${MONTHS[Number(m) - 1]}`; return t[0].toUpperCase() + t.slice(1); };
const errText = (e) => { const d = e?.response?.data?.detail; return typeof d === 'string' ? d : 'No pudimos completar la reserva. Intente de nuevo.'; };
const input = 'w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm focus:border-[#2F3D8E] focus:outline-none focus:ring-2 focus:ring-[#2F3D8E]/20';
const WINDOW_DAYS = 21;
const UTM = (() => { try { const q = new URLSearchParams(window.location.search); return Object.fromEntries(['source', 'medium', 'campaign', 'content'].map((k) => [k, q.get(`utm_${k}`) || '']).filter(([, v]) => v)); } catch { return {}; } })();

// v1.23.0 · Píxel de Meta (si está configurado en CRM › Publicación): PageView al abrir y Schedule + Lead al reservar,
// para medir cuántos clics de los anuncios llegan a la página y optimizar los anuncios a visitas o reservas.
function loadPixel(id) {
  if (!id || !/^\d{8,20}$/.test(String(id)) || window.fbq) return;
  /* eslint-disable */
  !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};
  if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;
  s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */
  window.fbq('init', String(id));
  window.fbq('track', 'PageView');
}
const track = (ev, data) => { try { if (window.fbq) window.fbq('track', ev, data); } catch { /* sin píxel */ } };

function LeadQuestions({ items, value, onChange }) {
  if (!items.length) return null;
  const set = (k, v) => onChange({ ...value, [k]: v });
  return (
    <fieldset className="space-y-3 rounded-xl border border-gray-200 bg-gray-50/60 p-3" data-testid="booking-lead">
      <legend className="sr-only">Ayúdenos a preparar la llamada (opcional)</legend>
      {/* v1.20.1 · título fuera de la leyenda: en 390 px la leyenda de dos líneas se montaba sobre el borde */}
      <p aria-hidden="true" className="text-sm font-bold text-[#0F2A3A]">Ayúdenos a preparar la llamada <span className="font-normal text-gray-500">(opcional)</span></p>
      {items.map((f) => (
        <div key={f.key} className="block text-sm font-semibold text-gray-700">
          {f.type === 'multiselect' ? (<>
            <span>{f.name}</span>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {f.options.map((o) => { const on = (value[f.key] || []).includes(o); return (
                <button type="button" key={o} onClick={() => set(f.key, on ? value[f.key].filter((x) => x !== o) : [...(value[f.key] || []), o])} aria-pressed={on}
                        className={`rounded-full border px-2.5 py-1 text-xs font-medium ${on ? 'border-[#2F3D8E] bg-[#2F3D8E] text-white' : 'border-gray-300 bg-white text-gray-700'}`}>{o}</button>); })}
            </div>
          </>) : (
            <label className="block">{f.name}
              {f.type === 'select'
                ? <select className={`${input} mt-1`} value={value[f.key] || ''} onChange={(e) => set(f.key, e.target.value)}><option value="">Elegir…</option>{f.options.map((o) => <option key={o} value={o}>{o}</option>)}</select>
                : <input className={`${input} mt-1`} inputMode={f.type === 'number' ? 'decimal' : undefined} maxLength={200} value={value[f.key] || ''} onChange={(e) => set(f.key, e.target.value)} />}
            </label>
          )}
        </div>
      ))}
    </fieldset>
  );
}

export default function BookingPage() {
  const { slug } = useParams();
  const [cal, setCal] = useState(null);
  const [error, setError] = useState('');
  const [from, setFrom] = useState(() => p(new Date().toISOString()).date);
  const [slots, setSlots] = useState(null);
  const [day, setDay] = useState('');
  const [slot, setSlot] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', company: '', notes: '', website: '' });
  const [questions, setQuestions] = useState([]);   // v1.19.0
  const [lead, setLead] = useState({});
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);
  const [formError, setFormError] = useState('');
  const [reload, setReload] = useState(0);

  useEffect(() => {
    axios.get(`${API}/${encodeURIComponent(slug)}`).then((r) => { setCal(r.data); loadPixel(r.data?.pixel_id); }).catch((e) => setError(errText(e)));
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/public/lead-fields`).then((r) => setQuestions(r.data.items || [])).catch(() => setQuestions([]));
  }, [slug]);
  useEffect(() => {
    if (!cal) return undefined;
    let alive = true;
    setSlots(null);
    axios.get(`${API}/${encodeURIComponent(slug)}/slots`, { params: { date_from: from, date_to: addDays(from, WINDOW_DAYS - 1) } })
      .then((r) => { if (alive) setSlots(r.data.slots); }).catch(() => { if (alive) setSlots([]); });
    return () => { alive = false; };
  }, [cal, slug, from, reload]);

  const byDay = useMemo(() => {
    const m = {};
    (slots || []).forEach((s) => { const k = p(s.start).date; (m[k] = m[k] || []).push(s); });
    return m;
  }, [slots]);
  const days = Object.keys(byDay).sort();
  useEffect(() => { if (days.length && !byDay[day]) setDay(days[0]); }, [days, byDay, day]);

  const book = async (e) => {
    e.preventDefault();
    setSending(true); setFormError('');
    try {
      const answers = Object.fromEntries(Object.entries(lead).filter(([, v]) => (Array.isArray(v) ? v.length : String(v || '').trim())));
      const r = await axios.post(`${API}/${encodeURIComponent(slug)}`, { ...form, start: slot.start, lead: answers, utm: UTM });
      setDone(r.data);
      track('Schedule', { content_name: slug }); track('Lead', { content_name: slug });   // v1.23.0
    } catch (err) {
      setFormError(errText(err));
      if (err?.response?.status === 409) { setSlot(null); setReload((n) => n + 1); }   // el horario se ocupó: volver a elegir
    } finally { setSending(false); }
  };

  const shell = (children) => (
    <div className="min-h-screen bg-[#EEF0F3] px-4 py-8">
      <div className="mx-auto max-w-3xl">
        <div className="mb-5 flex items-center gap-2">
          <img src="/logo_analytica.png" alt="Analytica" className="h-8 w-auto" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
        </div>
        <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm sm:p-7">{children}</div>
        <p className="mt-4 text-center text-xs text-gray-500">Horarios en hora de Ecuador (GMT-5).</p>
      </div>
    </div>
  );

  if (error) return shell(<div className="py-10 text-center"><h1 className="text-lg font-bold text-[#0F2A3A]">Página no disponible</h1><p className="mt-1 text-sm text-gray-600">{error}</p></div>);
  if (!cal) return shell(<p className="py-10 text-center text-sm text-gray-500">Cargando…</p>);
  if (done) {
    return shell(
      <div className="py-8 text-center" data-testid="booking-done">
        <CheckCircle2 className="mx-auto text-[#2E7A22]" size={44} />
        <h1 className="mt-3 text-xl font-extrabold text-[#0F2A3A]">¡Su cita está agendada!</h1>
        <p className="mt-2 text-gray-700">{done.calendar} · {done.when}</p>
        {/* v1.9.1 · el texto depende de si el correo de confirmación salió */}
        <p className="mt-1 text-sm text-gray-500">{done.confirmation_sent
          ? `Le enviamos la confirmación a ${form.email}. Si necesita cambiarla, responda ese correo.`
          : 'Guarde esta información. Si necesita cambiar la cita, comuníquese con nosotros.'}</p>
      </div>,
    );
  }

  return shell(
    <div data-testid="booking-page">
      <h1 className="text-xl font-extrabold text-[#0F2A3A] sm:text-2xl">{cal.name}</h1>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600">
        <span className="inline-flex items-center gap-1.5"><Clock size={15} /> {cal.duration} min</span>
        {cal.location_label && <span className="inline-flex items-center gap-1.5"><MapPin size={15} /> {cal.location_label}</span>}
      </div>
      {cal.description && <p className="mt-3 whitespace-pre-wrap text-sm text-gray-700">{cal.description}</p>}

      {!slot ? (
        <div className="mt-6">
          {formError && <p className="mb-3 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800" role="alert">{formError}</p>}
          <div className="mb-2 flex items-center justify-between">
            <h2 className="inline-flex items-center gap-1.5 text-sm font-bold text-[#0F2A3A]"><CalendarDays size={16} /> Elija día y hora</h2>
            <div className="flex gap-1">
              <button className="rounded-lg border border-gray-300 px-2.5 py-1 text-xs disabled:opacity-40" disabled={from <= p(new Date().toISOString()).date} onClick={() => setFrom(addDays(from, -WINDOW_DAYS))}>Anteriores</button>
              <button className="rounded-lg border border-gray-300 px-2.5 py-1 text-xs" onClick={() => setFrom(addDays(from, WINDOW_DAYS))}>Siguientes</button>
            </div>
          </div>
          {slots === null ? <p className="py-6 text-center text-sm text-gray-500">Buscando horarios…</p> : days.length === 0 ? (
            <p className="rounded-lg bg-gray-50 py-6 text-center text-sm text-gray-600">No hay horarios disponibles en estas fechas. Pruebe con «Siguientes».</p>
          ) : (
            <>
              <div className="flex gap-2 overflow-x-auto pb-2">
                {days.map((d) => (
                  <button key={d} onClick={() => setDay(d)} data-testid="booking-day"
                          className={`shrink-0 rounded-xl border px-3 py-2 text-center text-xs ${d === day ? 'border-[#2F3D8E] bg-[#2F3D8E] text-white' : 'border-gray-300 bg-white text-gray-700 hover:border-[#2F3D8E]'}`}>
                    <span className="block font-semibold">{DAYS[wd(d)][0].toUpperCase() + DAYS[wd(d)].slice(1, 3)}</span>
                    <span className="block text-base font-bold">{Number(d.slice(8))}</span>
                    <span className="block">{MONTHS[Number(d.slice(5, 7)) - 1].slice(0, 3)}</span>
                  </button>
                ))}
              </div>
              <div className="mt-3 text-sm font-semibold text-gray-700">{day && longDay(day)}</div>
              <div className="mt-2 grid grid-cols-3 gap-2 sm:grid-cols-5">
                {(byDay[day] || []).map((s) => (
                  <button key={s.start} onClick={() => setSlot(s)} data-testid="booking-slot"
                          className="rounded-lg border border-gray-300 bg-white py-2 text-sm font-semibold text-[#2F3D8E] hover:border-[#2F3D8E] hover:bg-[#2F3D8E]/5">{p(s.start).time}</button>
                ))}
              </div>
            </>
          )}
        </div>
      ) : (
        <form className="mt-6 space-y-3" onSubmit={book}>
          <button type="button" className="inline-flex items-center gap-1 text-sm font-semibold text-[#2F3D8E]" onClick={() => setSlot(null)}><ChevronLeft size={15} /> Cambiar horario</button>
          <div className="rounded-lg bg-[#2F3D8E]/5 px-3 py-2 text-sm font-semibold text-[#0F2A3A]">{longDay(p(slot.start).date)} · {p(slot.start).time}–{p(slot.end).time}</div>
          <label className="block text-sm font-semibold text-gray-700">Nombre y apellido
            <input className={`${input} mt-1`} required minLength={2} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoComplete="name" />
          </label>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block text-sm font-semibold text-gray-700">Correo
              <input type="email" className={`${input} mt-1`} required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} autoComplete="email" />
            </label>
            <label className="block text-sm font-semibold text-gray-700">Teléfono (opcional)
              <input type="tel" className={`${input} mt-1`} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} autoComplete="tel" />
            </label>
          </div>
          <label className="block text-sm font-semibold text-gray-700">Empresa (opcional)
            <input className={`${input} mt-1`} maxLength={120} value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} autoComplete="organization" />
          </label>
          <LeadQuestions items={questions} value={lead} onChange={setLead} />
          <label className="block text-sm font-semibold text-gray-700">¿Sobre qué quiere conversar? (opcional)
            <textarea className={`${input} mt-1`} rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </label>
          {/* trampa para robots: oculto a las personas */}
          <input type="text" name="website" tabIndex={-1} autoComplete="off" className="hidden" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} aria-hidden="true" />
          {formError && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">{formError}</p>}
          <button type="submit" disabled={sending} className="w-full rounded-lg bg-[#2F3D8E] py-3 text-sm font-bold text-white hover:bg-[#25316f] disabled:opacity-60" data-testid="booking-submit">
            {sending ? 'Agendando…' : 'Confirmar cita'}
          </button>
        </form>
      )}
    </div>,
  );
}
